import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../../api/admin';
import { tournamentsApi } from '../../api/tournaments';
import { Trophy, Calendar, DollarSign, Users, ArrowLeft, Percent, Award } from 'lucide-react';

const AdminTournamentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTournamentDetails();
  }, [id]);

  const loadTournamentDetails = async () => {
    setLoading(true);
    try {
      // Load tournament basic info
      const tournamentResponse = await tournamentsApi.getById(parseInt(id));
      if (tournamentResponse && tournamentResponse.success && tournamentResponse.tournament) {
        setTournament(tournamentResponse.tournament);
      }

      // Load detailed financial breakdown
      const detailsResponse = await adminApi.getTournamentDetails(parseInt(id));
      if (detailsResponse && detailsResponse.success && detailsResponse.details) {
        setDetails(detailsResponse.details);
      }
    } catch (error) {
      console.error('Error loading tournament details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-500/20 text-blue-400';
      case 'open': return 'bg-green-500/20 text-green-400';
      case 'in_progress': return 'bg-green-500/20 text-green-400 border-2 border-green-400';
      case 'closed': return 'bg-gray-500/20 text-gray-400';
      case 'close': return 'bg-gray-500/20 text-gray-400';
      case 'pending_payment': return 'bg-purple-500/20 text-purple-400';
      case 'completed': return 'bg-purple-500/20 text-purple-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'in_progress':
        return 'PROGRESS';
      case 'pending_payment':
        return 'PENDING PAYMENT';
      case 'close':
      case 'closed':
        return 'CLOSE';
      case 'upcoming':
        return 'UPCOMING';
      case 'open':
        return 'OPEN';
      default:
        return String(status || '').toUpperCase();
    }
  };

  const getRarityColor = (rarity) => {
    switch (rarity?.toLowerCase()) {
      case 'basic': return 'bg-gray-500/20 text-gray-400';
      case 'plus': return 'bg-blue-500/20 text-blue-400';
      case 'rare': return 'bg-purple-500/20 text-purple-400';
      case 'epic': return 'bg-orange-500/20 text-orange-400';
      case 'unique': return 'bg-yellow-500/20 text-yellow-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto"></div>
        <p className="mt-4 text-gray-400">Loading tournament details...</p>
      </div>
    );
  }

  if (!tournament || !details) {
    return (
      <div className="text-center py-12 text-gray-400">
        <p>Tournament not found</p>
        <button
          onClick={() => navigate('/admin/gameweeks')}
          className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg"
        >
          Back to GameWeeks
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-3xl font-bold flex items-center">
            <Trophy className="w-8 h-8 mr-3 text-yellow-400" />
            {tournament.name}
          </h1>
        </div>
        <div className="flex items-center space-x-3">
          <span className={`px-4 py-2 rounded-lg font-semibold ${getRarityColor(details.rarityRestriction)}`}>
            {details.rarityRestriction?.toUpperCase() || 'ALL'}
          </span>
          <span className={`px-4 py-2 rounded-lg font-semibold ${getStatusColor(tournament.status)}`}>
            {getStatusLabel(tournament.status)}
          </span>
        </div>
      </div>

      {/* Tournament Info */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center space-x-3">
            <Calendar className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-400">Start Date</p>
              <p className="text-white font-semibold">
                {new Date(details.startDate).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Calendar className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-400">End Date</p>
              <p className="text-white font-semibold">
                {new Date(details.endDate).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Users className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-400">Participants</p>
              <p className="text-white font-semibold text-xl">
                {details.participantsCount}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Financial Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Buy-in Breakdown */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h2 className="text-2xl font-bold mb-4 flex items-center">
            <DollarSign className="w-6 h-6 mr-2 text-yellow-400" />
            Buy-in Breakdown
          </h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gray-700/50 rounded-lg">
              <span className="text-gray-300">Total Buy-ins</span>
              <span className="text-white font-bold text-lg">{details.totalBuyIns.toFixed(2)} DIOS</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 flex items-center">
                  <Percent className="w-4 h-4 mr-2" />
                  Prize Pool (90%)
                </span>
                <span className="text-green-400 font-semibold">{details.prizePoolFromBuyIns.toFixed(2)} DIOS</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 flex items-center">
                  <Percent className="w-4 h-4 mr-2" />
                  Company Revenue (10%)
                </span>
                <span className="text-blue-400 font-semibold">{details.companyRevenueFromBuyIns.toFixed(2)} DIOS</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-gray-700">
                <span className="text-gray-400 flex items-center">
                  <Award className="w-4 h-4 mr-2" />
                  Referral Earnings (8/5/2 of company)
                </span>
                <span className="text-purple-400 font-semibold">-{details.totalReferralEarnings.toFixed(2)} DIOS</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-gray-700">
                <span className="text-gray-300 font-semibold">Net Company Revenue</span>
                <span className="text-white font-bold">{details.netCompanyRevenueFromBuyIns.toFixed(2)} DIOS</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Total Prize Pool */}
      <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-lg p-6 border-2 border-yellow-500/30">
        <h2 className="text-2xl font-bold mb-4 flex items-center">
          <Award className="w-6 h-6 mr-2 text-yellow-400" />
          Total Prize Pool
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-800/50 rounded-lg p-4">
            <p className="text-gray-400 text-sm mb-1">From Buy-ins (90%)</p>
            <p className="text-green-400 text-xl font-bold">{details.prizePoolFromBuyIns.toFixed(2)} DIOS</p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4">
            <p className="text-gray-400 text-sm mb-1">Other Contributions</p>
            <p className="text-blue-400 text-xl font-bold">{details.categoryContribution.toFixed(2)} DIOS</p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4">
            <p className="text-gray-400 text-sm mb-1">Guaranteed Prize</p>
            <p className="text-purple-400 text-xl font-bold">{details.guaranteedPrize.toFixed(2)} DIOS</p>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-yellow-500/30">
          <div className="flex justify-between items-center">
            <span className="text-white text-xl font-semibold">Total Prize Pool</span>
            <span className="text-yellow-400 text-3xl font-bold">{details.totalPrizePool.toFixed(2)} DIOS</span>
          </div>
        </div>
      </div>

    </div>
  );
};

export default AdminTournamentDetail;

